import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FaDownload,
  FaEye,
  FaFileAlt,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFileWord,
  FaSearch,
  FaTrash,
  FaUpload,
} from "react-icons/fa";

import documentService from "../services/documentService";

const CATEGORY_OPTIONS_BY_MODULE = {
  ouvrier: [
    { value: "cin", label: "CIN" },
    { value: "contrat", label: "Contrat" },
    { value: "cnss", label: "CNSS" },
    { value: "rib", label: "RIB" },
    { value: "photo", label: "Photo" },
    { value: "autre", label: "Autre" },
  ],
  materiel: [
    { value: "assurance", label: "Assurance" },
    { value: "carte_grise", label: "Carte grise" },
    { value: "visite_technique", label: "Visite technique" },
    { value: "vignette", label: "Vignette" },
    { value: "feuille_circulation", label: "Feuille de circulation" },
    { value: "mouchard", label: "Mouchard" },
    { value: "permis_conducteur", label: "Permis conducteur" },
    { value: "photo", label: "Photo" },
    { value: "facture", label: "Facture" },
    { value: "vidange", label: "Vidange" },
    { value: "reparation", label: "Réparation" },
    { value: "contrat", label: "Contrat" },
    { value: "autre", label: "Autre" },
  ],
  chantier: [
    { value: "plan", label: "Plan" },
    { value: "autorisation", label: "Autorisation" },
    { value: "photo", label: "Photo" },
    { value: "facture", label: "Facture" },
    { value: "contrat", label: "Contrat" },
    { value: "autre", label: "Autre" },
  ],
  pointage: [
    { value: "justificatif", label: "Justificatif" },
    { value: "photo", label: "Photo" },
    { value: "autre", label: "Autre" },
  ],
  pointage_materiel: [
    { value: "justificatif", label: "Justificatif" },
    { value: "photo", label: "Photo" },
    { value: "autre", label: "Autre" },
  ],
  vidange: [
    { value: "facture", label: "Facture" },
    { value: "photo", label: "Photo" },
    { value: "controle", label: "Contrôle" },
    { value: "autre", label: "Autre" },
  ],
  reparation: [
    { value: "facture", label: "Facture" },
    { value: "photo", label: "Photo" },
    { value: "rapport", label: "Rapport" },
    { value: "autre", label: "Autre" },
  ],
  citerne_gasoil: [
    { value: "facture", label: "Facture" },
    { value: "photo", label: "Photo" },
    { value: "controle", label: "Contrôle" },
    { value: "livraison", label: "Livraison" },
    { value: "autre", label: "Autre" },
  ],
  abonnement: [
    { value: "contrat", label: "Contrat" },
    { value: "facture", label: "Facture" },
    { value: "recu", label: "Reçu" },
    { value: "rib", label: "RIB" },
    { value: "autre", label: "Autre" },
  ],
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const PDF_EXTENSIONS = ["pdf"];
const EXCEL_EXTENSIONS = ["xls", "xlsx", "csv"];
const WORD_EXTENSIONS = ["doc", "docx"];

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "-";
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR");
};

const getDocType = (ext = "") => {
  const normalized = String(ext || "").toLowerCase();

  if (IMAGE_EXTENSIONS.includes(normalized)) {
    return {
      kind: "image",
      icon: <FaFileImage />,
      badge: <Badge bg="info">Image</Badge>,
    };
  }

  if (PDF_EXTENSIONS.includes(normalized)) {
    return {
      kind: "pdf",
      icon: <FaFilePdf />,
      badge: <Badge bg="danger">PDF</Badge>,
    };
  }

  if (EXCEL_EXTENSIONS.includes(normalized)) {
    return {
      kind: "excel",
      icon: <FaFileExcel />,
      badge: <Badge bg="success">Excel</Badge>,
    };
  }

  if (WORD_EXTENSIONS.includes(normalized)) {
    return {
      kind: "word",
      icon: <FaFileWord />,
      badge: <Badge bg="primary">Word</Badge>,
    };
  }

  return {
    kind: "other",
    icon: <FaFileAlt />,
    badge: <Badge bg="secondary">Fichier</Badge>,
  };
};

const DocumentsPanel = ({
  module,
  moduleId,
  title = "Documents",
  canDelete = true,
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [file, setFile] = useState(null);
  const [categorie, setCategorie] = useState("");
  const [description, setDescription] = useState("");

  const [search, setSearch] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("");

  const [messageError, setMessageError] = useState("");
  const [messageSuccess, setMessageSuccess] = useState("");

  const [previewUrls, setPreviewUrls] = useState({});
  const [previewLoadingIds, setPreviewLoadingIds] = useState({});

  const categoryOptions = useMemo(() => {
    return (
      CATEGORY_OPTIONS_BY_MODULE[String(module || "").toLowerCase()] || [
        { value: "autre", label: "Autre" },
      ]
    );
  }, [module]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setMessageError("");
      const res = await documentService.getDocumentsByModule(module, moduleId);
      setDocuments(res.data || []);
    } catch (err) {
      console.error("❌ Erreur chargement documents", err);
      setMessageError("❌ Erreur lors du chargement des documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (module && moduleId) {
      loadDocuments();
    }
  }, [module, moduleId]);

  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        if (url) window.URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  const filteredDocs = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return (documents || []).filter((doc) => {
      const matchesSearch =
        !q ||
        String(doc.nom_original || "").toLowerCase().includes(q) ||
        String(doc.categorie || "").toLowerCase().includes(q) ||
        String(doc.description || "").toLowerCase().includes(q) ||
        String(doc.uploader_name || "").toLowerCase().includes(q);

      const matchesCategory =
        !filterCategorie || String(doc.categorie || "") === filterCategorie;

      return matchesSearch && matchesCategory;
    });
  }, [documents, search, filterCategorie]);

  const ensurePreviewUrl = async (doc) => {
    if (!doc?.id) return null;

    const type = getDocType(doc.extension);
    if (!["image", "pdf"].includes(type.kind)) return null;
    if (previewUrls[doc.id]) return previewUrls[doc.id];

    try {
      setPreviewLoadingIds((prev) => ({ ...prev, [doc.id]: true }));
      const url = await documentService.getDocumentObjectUrl(doc.id);
      setPreviewUrls((prev) => ({ ...prev, [doc.id]: url }));
      return url;
    } catch (err) {
      console.error("❌ Erreur preview document", err);
      setMessageError("❌ Impossible de prévisualiser ce document.");
      return null;
    } finally {
      setPreviewLoadingIds((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessageError("⚠️ Sélectionne un fichier.");
      return;
    }

    try {
      setUploading(true);
      setMessageError("");
      setMessageSuccess("");

      const res = await documentService.uploadDocument({
        file,
        module,
        moduleId,
        categorie,
        description,
      });

      setFile(null);
      setCategorie("");
      setDescription("");
      setMessageSuccess(res?.message || "✅ Document ajouté avec succès.");

      const fileInput = document.getElementById("documents-panel-file-input");
      if (fileInput) fileInput.value = "";

      await loadDocuments();
    } catch (err) {
      console.error("❌ Erreur upload", err);
      setMessageError(
        err?.response?.data?.message || "❌ Erreur lors de l'upload du document."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;

    try {
      setMessageError("");
      setMessageSuccess("");

      await documentService.deleteDocument(id);
      setMessageSuccess("✅ Document supprimé avec succès.");

      if (previewUrls[id]) {
        window.URL.revokeObjectURL(previewUrls[id]);
        setPreviewUrls((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }

      await loadDocuments();
    } catch (err) {
      console.error("❌ Erreur suppression", err);
      setMessageError(
        err?.response?.data?.message || "❌ Erreur lors de la suppression."
      );
    }
  };

  const handleDownload = async (doc) => {
    try {
      setMessageError("");
      await documentService.downloadDocument(doc.id, doc.nom_original);
    } catch (err) {
      console.error("❌ Erreur download", err);
      setMessageError("❌ Impossible de télécharger ce document.");
    }
  };

  const handlePreview = async (doc) => {
    try {
      setMessageError("");
      const url = await ensurePreviewUrl(doc);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("❌ Erreur preview", err);
      setMessageError("❌ Impossible d'ouvrir ce document.");
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 className="mb-0">
          📂 {title} <Badge bg="dark">{documents.length}</Badge>
        </h5>
      </div>

      {messageError && <Alert variant="danger">{messageError}</Alert>}
      {messageSuccess && <Alert variant="success">{messageSuccess}</Alert>}

      <Row className="g-2 mb-3">
        <Col md={4}>
          <Form.Control
            id="documents-panel-file-input"
            type="file"
            disabled={uploading}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Col>

        <Col md={3}>
          <Form.Select
            value={categorie}
            disabled={uploading}
            onChange={(e) => setCategorie(e.target.value)}
          >
            <option value="">Catégorie</option>
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Control
            placeholder="Description"
            value={description}
            disabled={uploading}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <Button
            variant="primary"
            className="w-100"
            onClick={handleUpload}
            disabled={uploading || !file}
          >
            {uploading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Upload...
              </>
            ) : (
              <>
                <FaUpload className="me-2" />
                Upload
              </>
            )}
          </Button>
        </Col>
      </Row>

      <Row className="g-2 mb-3">
        <Col md={6}>
          <div className="position-relative">
            <FaSearch
              style={{
                position: "absolute",
                left: 12,
                top: 12,
                opacity: 0.6,
              }}
            />
            <Form.Control
              style={{ paddingLeft: 36 }}
              placeholder="Rechercher par nom, catégorie, description, utilisateur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Col>

        <Col md={3}>
          <Form.Select
            value={filterCategorie}
            onChange={(e) => setFilterCategorie(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {categoryOptions.map((opt) => (
              <option key={`filter-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3} className="d-flex align-items-center">
          <Badge bg="secondary">
            Résultats : {filteredDocs.length}
          </Badge>
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" />
          <span>Chargement des documents...</span>
        </div>
      ) : (
        <Table bordered hover responsive size="sm" className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 90 }}>Preview</th>
              <th style={{ width: 100 }}>Type</th>
              <th>Nom</th>
              <th>Catégorie</th>
              <th>Description</th>
              <th>Taille</th>
              <th>Ajouté par</th>
              <th>Date</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredDocs.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center">
                  Aucun document trouvé.
                </td>
              </tr>
            )}

            {filteredDocs.map((doc) => {
              const docType = getDocType(doc.extension);
              const canPreview = ["image", "pdf"].includes(docType.kind);
              const previewUrl = previewUrls[doc.id];

              return (
                <tr key={doc.id}>
                  <td className="text-center">
                    {docType.kind === "image" ? (
                      previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={doc.nom_original}
                          style={{
                            width: 52,
                            height: 52,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid #ddd",
                            cursor: "pointer",
                          }}
                          onClick={() => handlePreview(doc)}
                        />
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => handlePreview(doc)}
                          disabled={!!previewLoadingIds[doc.id]}
                        >
                          {previewLoadingIds[doc.id] ? (
                            <Spinner size="sm" />
                          ) : (
                            "Voir"
                          )}
                        </Button>
                      )
                    ) : canPreview ? (
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => handlePreview(doc)}
                        disabled={!!previewLoadingIds[doc.id]}
                      >
                        {previewLoadingIds[doc.id] ? (
                          <Spinner size="sm" />
                        ) : (
                          "Voir"
                        )}
                      </Button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>

                  <td className="text-center">
                    <div className="d-flex flex-column align-items-center gap-1">
                      <div style={{ fontSize: 18 }}>{docType.icon}</div>
                      {docType.badge}
                    </div>
                  </td>

                  <td>{doc.nom_original || doc.nom_fichier || "-"}</td>

                  <td>
                    <Badge bg="secondary">{doc.categorie || "-"}</Badge>
                  </td>

                  <td>{doc.description || "-"}</td>

                  <td>{formatFileSize(doc.taille)}</td>

                  <td>{doc.uploader_name || doc.uploaded_by || "-"}</td>

                  <td>{formatDateTime(doc.created_at)}</td>

                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      {canPreview && (
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => handlePreview(doc)}
                          title="Prévisualiser"
                        >
                          <FaEye />
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleDownload(doc)}
                        title="Télécharger"
                      >
                        <FaDownload />
                      </Button>

                      {canDelete && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDelete(doc.id)}
                          title="Supprimer"
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default DocumentsPanel;